import { type StructuredText as StructuredTextType } from 'datocms-structured-text-utils'
import { graphql } from 'gatsby'
import React, { type ReactElement, useContext } from 'react'
import { Col, Container, Row } from 'react-bootstrap'

import Animation from '@/components/animation/animation'
import { Cta } from '@/components/blocks-library/cta/cta'
import Image from '@/components/image/image'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import { ColorContext } from '@/contexts/color.context'

import * as styles from './styles.module.scss'

type UseCase = {
  title: string | null
  image: {
    readonly gatsbyImageData: Record<string, unknown> | null
    readonly title: string | null
    readonly url: string | null
    readonly alt: string | null
  } | null
  content: { readonly value: Record<string, unknown> | null } | null
  moreInfo: Queries.CtaFragment | null
}

const UseCaseSection = (
  props: Queries.UseCaseSectionFragment
): ReactElement | null => {
  const { title, items } = props
  return (
    <Container className={styles.useCaseSection}>
      <h2>{title}</h2>
      <div className={styles.rowItems}>
        {items?.map((item) => {
          if (item === null) return null
          const { id, title, image, content, moreInfo } = item
          const cta = moreInfo !== null ? moreInfo[0] : null
          return (
            <UseCaseBlock
              title={title}
              moreInfo={cta}
              image={image}
              content={content}
              key={id}
            />
          )
        })}
      </div>
    </Container>
  )
}

const UseCaseBlock = ({
  title,
  moreInfo,
  image,
  content
}: UseCase): ReactElement | null => {
  const colour = useContext(ColorContext)
  const borderColor = { borderColor: colour?.colour?.hex }
  return (
    <Row className="mb-5 pb-4 gx-lg-5 pt-lg-4">
      <Col lg={6} className="mb-5">
        <h3>{title}</h3>

        <StructuredTextDefault
          content={content as unknown as StructuredTextType}
        />
        {moreInfo !== null && (
          <div className="text-center text-md-start">
            <Cta {...moreInfo} className="mt-4" />
          </div>
        )}
      </Col>
      <Col lg={6} className="mt-3">
        <div className={`${styles.borderImage}`}>
          {image !== null && image !== undefined && (
            <Image alt={image.alt ?? ''} image={image} />
          )}
          <Animation>
            <span>{image?.title}</span>
          </Animation>
          <span className={styles.borderSpan} style={borderColor}></span>
        </div>
      </Col>
    </Row>
  )
}

export default UseCaseSection

export const query = graphql`
  fragment UseCaseSection on DatoCmsUseCaseSection {
    __typename
    id
    navigationLabel
    title
    items {
      id
      title
      image {
        gatsbyImageData
        title
        url
        alt
      }
      content {
        value
      }
      moreInfo {
        ... on DatoCmsCta {
          ...Cta
        }
      }
    }
  }
`
